import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Switch, message } from 'antd';
import {
  ShareAltOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { DEFAULT_MODAL_RADIUS } from '../constants/styles';

const { TextArea, Password } = Input;

export interface CloudShareModalProps {
  visible: boolean;
  onCancel: () => void;
  /** 分享类型 */
  type: 'cloudTag' | 'cloudTagGroup';
  /** 项目 ID */
  itemId: string;
  /** 项目名称 */
  itemName: string;
  /** 是否已公开（区分首次分享/更新分享） */
  isPublic: boolean;
  /** 当前别名 */
  currentAlias?: string | null;
  /** 当前描述 */
  currentDescription?: string | null;
  /** 当前是否匿名 */
  currentIsAnonymous?: boolean;
  /** 当前是否加密 */
  currentIsEncrypted?: boolean;
  /** 分享回调 */
  onShare: (params: {
    isPublic: boolean;
    alias?: string;
    description?: string;
    isAnonymous?: boolean;
    isEncrypted?: boolean;
    accessPassword?: string;
  }) => Promise<void>;
  /** 取消分享回调 */
  onUnshare?: () => Promise<void>;
}

/**
 * 云剪藏分享弹窗组件
 * 用于分享/更新分享/取消分享 CloudTag/CloudTagGroup
 */
export const CloudShareModal: React.FC<CloudShareModalProps> = ({
  visible,
  onCancel,
  type,
  itemId,
  itemName,
  isPublic,
  currentAlias,
  currentDescription,
  currentIsAnonymous = false,
  currentIsEncrypted = false,
  onShare,
  onUnshare,
}) => {
  const [alias, setAlias] = useState('');
  const [description, setDescription] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [accessPassword, setAccessPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [unshareLoading, setUnshareLoading] = useState(false);

  const typeLabel = type === 'cloudTagGroup' ? '标签组' : '标签';
  const isUpdate = isPublic;

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      setAlias(currentAlias || '');
      setDescription(currentDescription || '');
      setIsAnonymous(currentIsAnonymous);
      setIsEncrypted(currentIsEncrypted);
      setAccessPassword('');
    }
  }, [visible, currentAlias, currentDescription, currentIsAnonymous, currentIsEncrypted]);

  const handleConfirm = async () => {
    if (isEncrypted && !accessPassword && !currentIsEncrypted) {
      message.warning('请输入访问密码');
      return;
    }

    setLoading(true);
    try {
      await onShare({
        isPublic: true,
        alias: alias.trim() || undefined,
        description: description.trim() || undefined,
        isAnonymous,
        isEncrypted,
        accessPassword: isEncrypted ? accessPassword : undefined,
      });
      handleClose();
    } catch (error) {
      console.error('[CloudShareModal] Share failed:', error);
      message.error('分享失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleUnshare = async () => {
    if (!onUnshare) return;

    setUnshareLoading(true);
    try {
      await onUnshare();
      handleClose();
    } catch (error) {
      console.error('[CloudShareModal] Unshare failed:', error);
      message.error('取消分享失败，请重试');
    } finally {
      setUnshareLoading(false);
    }
  };

  const handleClose = () => {
    setAlias('');
    setDescription('');
    setIsAnonymous(false);
    setIsEncrypted(false);
    setAccessPassword('');
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isUpdate ? (
            <EditOutlined style={{ fontSize: 20, color: '#1890ff' }} />
          ) : (
            <ShareAltOutlined style={{ fontSize: 20, color: '#1890ff' }} />
          )}
          <span>
            {isUpdate ? '更新分享' : '公开到广场'} - {typeLabel}
            <span style={{ color: 'var(--ant-color-text-secondary)', fontWeight: 400, marginLeft: 4 }}>「{itemName}」</span>
          </span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={500}
      maskClosable={!loading && !unshareLoading}
      closable={!loading && !unshareLoading}
      styles={{
        body: {
          paddingTop: 16,
        },
        mask: {
          backdropFilter: 'blur(4px)',
        },
      }}
      className="cloud-share-modal"
    >
      {/* 别名输入 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <span>广场展示标题</span>
          <span
            style={{
              color: 'var(--ant-color-text-tertiary)',
              marginLeft: 8,
              fontSize: 12,
            }}
          >
            （选填，不填则使用原名称）
          </span>
        </div>
        <Input
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          placeholder={`自定义${typeLabel}在广场上的展示名称`}
          maxLength={100}
        />
      </div>

      {/* 描述输入 */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <span>描述</span>
          <span
            style={{
              color: 'var(--ant-color-text-tertiary)',
              marginLeft: 8,
              fontSize: 12,
            }}
          >
            （选填）
          </span>
        </div>
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={`添加一段描述，让其他用户更了解这个${typeLabel}...`}
          rows={3}
          maxLength={500}
          showCount
        />
      </div>

      {/* 匿名开关 */}
      <div
        style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: 'var(--ant-color-fill-secondary)',
          borderRadius: 8,
          border: '1px solid var(--ant-color-split)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EyeInvisibleOutlined
              style={{ fontSize: 16, color: 'var(--ant-color-text-secondary)' }}
            />
            <div>
              <div style={{ fontWeight: 500, marginBottom: 2 }}>匿名发布</div>
              <div style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
                {isAnonymous ? '将以"匿名用户"的身份分享' : '将显示你的用户名'}
              </div>
            </div>
          </div>
          <Switch checked={isAnonymous} onChange={setIsAnonymous} />
        </div>
      </div>

      {/* 加密开关 */}
      <div
        style={{
          marginBottom: 24,
          padding: '12px 16px',
          background: 'var(--ant-color-fill-secondary)',
          borderRadius: 8,
          border: '1px solid var(--ant-color-split)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LockOutlined
              style={{ fontSize: 16, color: 'var(--ant-color-text-secondary)' }}
            />
            <div>
              <div style={{ fontWeight: 500, marginBottom: 2 }}>加密分享</div>
              <div style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
                {isEncrypted ? '需要密码才能查看内容' : '所有人可以直接查看'}
              </div>
            </div>
          </div>
          <Switch checked={isEncrypted} onChange={setIsEncrypted} />
        </div>

        {/* 密码输入框 */}
        {isEncrypted && (
          <div style={{ marginTop: 12 }}>
            <Password
              value={accessPassword}
              onChange={(e) => setAccessPassword(e.target.value)}
              placeholder={currentIsEncrypted ? '留空则保持原密码' : '设置访问密码'}
              maxLength={32}
            />
          </div>
        )}
      </div>

      {/* 按钮区 */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          {isUpdate && onUnshare && (
            <Button
              danger
              onClick={handleUnshare}
              loading={unshareLoading}
              disabled={loading}
            >
              取消分享
            </Button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={handleClose} disabled={loading || unshareLoading}>
            取消
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={unshareLoading}
            icon={isUpdate ? <EditOutlined /> : <ShareAltOutlined />}
          >
            {isUpdate ? '更新信息' : '公开到广场'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CloudShareModal;
